import React, { useEffect, useRef, useState } from 'react';
import { useReducedMotion } from 'framer-motion';

/**
 * AnimatedNumber
 *
 * Elegantly counts a number up from 0 to `value` over `duration` ms.
 * UX purpose: convey data richness — a static number reads as "already there",
 * an animated one signals "the system computed this for you now".
 *
 * Respects prefers-reduced-motion → renders the final value immediately.
 *
 * Props:
 *   value      number to count to (can be null / undefined → renders `placeholder`)
 *   duration   ms, default 1200
 *   suffix     '%', 's', '/100', etc.
 *   prefix     '$', '+', etc.
 *   decimals   digits after the decimal
 *   format     'compact' → 12.4K, 1.2M ; 'default' → thousand separators
 *   placeholder string when value is null. Default '—'.
 */
const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);

const compactFormat = (n) => {
  const abs = Math.abs(n);
  if (abs >= 1e6) return (n / 1e6).toFixed(1).replace(/\.0$/, '') + 'M';
  if (abs >= 1e3) return (n / 1e3).toFixed(1).replace(/\.0$/, '') + 'K';
  return Math.round(n).toString();
};

const AnimatedNumber = ({
  value,
  duration = 1200,
  suffix = '',
  prefix = '',
  decimals = 0,
  format = 'default',
  placeholder = '—',
  className = '',
  testid,
}) => {
  const reduce = useReducedMotion();
  const [display, setDisplay] = useState(value == null ? null : (reduce ? value : 0));
  const rafRef = useRef();
  const fromRef = useRef(0);
  const toRef = useRef(value ?? 0);

  useEffect(() => {
    if (value == null) { setDisplay(null); return; }
    if (reduce) { setDisplay(value); return; }
    fromRef.current = typeof display === 'number' ? display : 0;
    toRef.current = value;
    const start = performance.now();
    const step = (now) => {
      const t = Math.min(1, (now - start) / duration);
      const eased = easeOutCubic(t);
      setDisplay(fromRef.current + (toRef.current - fromRef.current) * eased);
      if (t < 1) rafRef.current = requestAnimationFrame(step);
    };
    rafRef.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(rafRef.current);
  }, [value, duration, reduce]);

  if (display == null) {
    return (
      <span className={className} data-testid={testid}>{placeholder}</span>
    );
  }
  const num = decimals > 0 ? display.toFixed(decimals) : Math.round(display);
  const formatted = format === 'compact'
    ? compactFormat(Number(num))
    : Number(num).toLocaleString(undefined, { maximumFractionDigits: decimals });

  return (
    <span className={`tabular-nums ${className}`} data-testid={testid}>
      {prefix}{formatted}{suffix}
    </span>
  );
};

export default AnimatedNumber;
